import { CommonModule } from '@angular/common';
import {
    AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject,
    input, numberAttribute, OnDestroy, output, signal
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { switchMap, throwError } from 'rxjs';

import { HttpApiError } from '@models/api/http-api-error';
import { Guid } from '@models/base/helper-types';
import { VzFile } from '@models/files/file';
import { Upload } from '@models/files/upload';
import { observeResize } from '@models/utils/dom-ui';
import { ErrorPanelComponent } from '@utils/error-panel';
import { PublicApiService } from '@utils/api.service';
import { BaseMin } from '@utils/base-min';

@Component({
    selector: 'vz-image-preview, [vz-image-preview]',
    template: `
        <div class="vz-image-preview-wrapper mv05" [class.vz-ipw-clickable]="clickable()">
            @if (error()) {
                <vz-error-panel [error]="error()" />
            }
            @else {
                <div class="relative bc b1s br4 of-h"
                    [style.width.px]="size()[0]"
                    [style.height.px]="size()[1]"
                    (click)="clickable() ? previewClick.emit({ e: $event, f: file() }) : undefined">
                    @if (file()) {
                        @if (preview(); as prev) {
                            <img style="width: 100%" [src]="prev" (load)="updateTempPreview(tmpImg.naturalWidth, tmpImg.naturalHeight)" #tmpImg />
                        }
                        @else {
                            <img style="width: 100%" [src]="fileSrc()" (load)="updateTempPreview(prevImg.naturalWidth, prevImg.naturalHeight)" #prevImg />
                        }
                    }
                    @if (_loading()) {
                        <div class="layout center-center fit bg op05">
                            <div class="loader small2"></div>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        @import 'theme';
        .vz-image-preview-wrapper.vz-ipw-clickable > div:hover {
            border-color: @accentColor !important;
            cursor: pointer;
        }
    `,
    imports: [CommonModule, ErrorPanelComponent]
})
export class ImageViewComponent extends BaseMin implements OnDestroy, AfterViewInit {

    readonly fileId     = input<Guid>();
    readonly maxW       = input<number, unknown>(1200, { transform: numberAttribute });
    readonly maxH       = input<number, unknown>(400, { transform: numberAttribute });
    readonly clickable  = input<boolean, unknown>(false, { transform: booleanAttribute });

    readonly previewClick   = output<{ e: MouseEvent, f?: VzFile }>();

    readonly file       = signal<VzFile | undefined>(undefined);
    readonly size       = signal<number[]>([40, 40]);
    readonly pSize      = signal<number[]>([40, 40]);
    readonly error      = signal<HttpApiError | undefined>(undefined);
    readonly rnd        = signal<string>('');
    readonly preview    = signal<SafeUrl | undefined>(undefined);
    readonly upload     = signal<Upload | undefined>(undefined);
    readonly fileSrc    = signal<SafeUrl | undefined>(undefined);

    private readonly _elRef         = inject(ElementRef);
    private readonly _destroyRef    = inject(DestroyRef);
    private readonly _sanitizer     = inject(DomSanitizer);
    private readonly _api           = inject(PublicApiService);

    constructor() {
        super();
        effect(() => {
            const fid = this.fileId();
            this.__unsubscribe();
            if (fid) {
                this.preview.set(undefined);
                this.loadFileInfo();
            }
            this.checkLayout();
        });
    }

    ngAfterViewInit(): void {
        observeResize(this._destroyRef, this._elRef.nativeElement.firstElementChild, 300).subscribe(() => this.checkLayout());
    }

    checkLayout(): void {
        // console.warn('==[ImageViewComponent]=====> checkLayout');
        const r = this._elRef?.nativeElement.getBoundingClientRect();
        const cw = r?.width || 40;
        const f = { width: this.pSize()[0], height: this.pSize()[1] };
        // console.groupCollapsed('==[ImageViewComponent]=====> checkLayout', f);
        // console.log('==[ImageViewComponent]=====> checkLayout, r:', r, 'cw:', cw);
        let width = 40;
        let height = 40;
        if (f && f.width > 0 && f.height > 0) {
            width = Math.min(f.width, cw);
            height = Math.max(f.height, 40);
            if (width < f.width) {
                height = f.height * width / f.width;
            }
        }
        const s = this.size();
        // console.log('==[ImageViewComponent]=====> checkLayout, s:', s);
        if (s[0] !== width || s[1] !== height) {
            // console.log('==[ImageViewComponent]=====> checkLayout, size.set:', [width, height]);
            this.size.set([width, height]);
            this._Changed();
        }
        // console.groupEnd();
    }

    loadFileInfo(update?: boolean): void {
        const id = this.fileId();
        if (id) {
            this._api.getFile(id).subscribe(info => {
                if (info) {
                    const f = this.file();
                    if (f && f.imgView && this.upload()) {
                        info.parse({ imgView: f.imgView });
                        this._loading.set(false);
                    }
                    this.file.set(info);
                    this.updateTempPreview(info.imgView?.width || 40, info.imgView?.height || 40);
                    if (update && !this.rnd()) {
                        this.rnd.set(this.__CID);
                    }
                    this.checkLayout();
                    if (info?.imgView) {
                        this._loading.set(true);
                        this.fileSrc.set(undefined);
                        this._api.getFileDownloadLink(id).pipe(
                            switchMap(urls => urls?.previewUrl ? this._api.getUrl(urls.previewUrl) : throwError(() => 'There is no preview for that file.'))
                        ).subscribe({
                            next: blob => {
                                if (blob) {
                                    this.fileSrc.set(this._sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob)));
                                }
                                else {
                                    this.error.set(new HttpApiError('Не удалось загрузить содержимое файла', 'Ошибка декодирования.'));
                                }
                                this.imageLoaded();
                            },
                            error: err => {
                                this.error.set(err);
                                this.imageLoaded();
                            }
                        });
                    }
                }
            });
        }
        this._Changed();
    }

    imageLoaded(): void {
        this._loading.set(false);
        this._Changed();
    }

    handleImageLoadError(e: any): void {
        if (!this.rnd()) {
            this.loadFileInfo(true);
        }
    }

    updateTempPreview(w: number, h: number): void {
        // console.log('==[ImageViewComponent]=====> updateTempPreview:', [w, h]);
        let width = w;
        let height = h;
        const kw = width / this.maxW();
        const kh = height / this.maxH();
        if (kw > kh && width > this.maxW()) {
            width = this.maxW();
            height = height * width / w;
        }
        else if (height > this.maxH()) {
            height = this.maxH();
            width = width * height / h;
        }
        this.pSize.set([width, height]);
        this._Changed();
    }

}